import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronRight } from '@fortawesome/free-solid-svg-icons'
import styles from './ResponsiveTable.module.scss'

/**
 * CardListItem - Renders a single data row as a tappable card on mobile.
 *
 * Uses the column priority config to map data into card anatomy:
 * header (badges), title, subtitle, body (key-value grid), expandable, footer.
 */
const CardListItem = ({
  row,
  rowIndex,
  columns,
  priorityConfig,
  onRowClick,
  animationDelay
}) => {
  const [expanded, setExpanded] = useState(false)

  const classified = columns.reduce((acc, col) => {
    const config = priorityConfig[col.id]
    if (!config) return acc
    if (config.priority === 4 || config.mobileRole === null) return acc
    if (config.priority === 'action') return acc

    const role = config.mobileRole
    if (!acc[role]) acc[role] = []
    acc[role].push(col)
    return acc
  }, {})

  const titleCol = (classified.title || [])[0]
  const subtitleCol = (classified.subtitle || [])[0]
  const badgeCols = classified.badge || []
  const fieldCols = classified.field || []
  const expandableCols = classified.expandable || []
  const footerCols = classified.footer || []

  const renderValue = (col) => {
    if (col.renderCell) return col.renderCell(row, rowIndex)
    return row[col.id] || '\u2014'
  }

  const handleClick = () => {
    if (onRowClick) onRowClick(row, rowIndex)
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleClick()
    }
  }

  const toggleExpand = (e) => {
    e.stopPropagation()
    setExpanded((prev) => !prev)
  }

  const titleText = titleCol && row[titleCol.id] ? String(row[titleCol.id]) : ''
  const subtitleText = subtitleCol && row[subtitleCol.id] ? String(row[subtitleCol.id]) : ''
  const ariaLabel = [titleText, subtitleText].filter(Boolean).join(' - ')

  const delayMs = Math.min(rowIndex, 10) * 30

  return (
    <div
      className={styles.card}
      style={{ '--card-index': rowIndex, animationDelay: animationDelay || `${delayMs}ms` }}
      role='button'
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      aria-label={ariaLabel || `Row ${rowIndex + 1}`}
    >
      {/* Header: badges + chevron */}
      <div className={styles.card__header}>
        <div className={styles.card__badges}>
          {badgeCols.map((col) => (
            <span key={col.id} className={styles.card__badge}>
              {renderValue(col)}
            </span>
          ))}
        </div>
        <FontAwesomeIcon icon={faChevronRight} className={styles.card__chevron} />
      </div>

      {/* Title */}
      {titleCol && (
        <div className={styles.card__title}>
          {renderValue(titleCol)}
        </div>
      )}

      {/* Subtitle */}
      {subtitleCol && (
        <div className={styles.card__subtitle}>
          {renderValue(subtitleCol)}
        </div>
      )}

      {/* Body: key-value grid */}
      {fieldCols.length > 0 && (
        <div className={styles.card__body}>
          {fieldCols.map((col) => (
            <div key={col.id} className={styles.card__field}>
              <span className={styles.card__fieldLabel}>{col.label}</span>
              <span className={styles.card__fieldValue}>{renderValue(col)}</span>
            </div>
          ))}
        </div>
      )}

      {/* Expandable: long text (call notes, failed criteria) */}
      {expandableCols.length > 0 && expandableCols.map((col) => {
        const rawVal = row[col.id]
        if (!rawVal || (typeof rawVal === 'string' && !rawVal.trim())) return null
        const val = renderValue(col)
        const isString = typeof val === 'string'
        const isEmpty = !val || (isString && (!val.trim() || val.trim() === '\u2014'))
        if (isEmpty) return null
        const isLong = isString && val.length > 100

        return (
          <div key={col.id} className={styles.card__expandable}>
            <span className={styles.card__fieldLabel}>{col.label}</span>
            <div className={expanded || !isLong ? styles.card__expandedText : styles.card__truncatedText}>
              {val}
            </div>
            {isLong && (
              <button
                type='button'
                className={styles.card__moreButton}
                onClick={toggleExpand}
              >
                {expanded ? 'Show less' : 'Show more'}
              </button>
            )}
          </div>
        )
      })}

      {/* Footer: scores, status — skip if all values are empty */}
      {footerCols.length > 0 && (() => {
        const hasContent = footerCols.some((col) => {
          const raw = row[col.id]
          return raw !== undefined && raw !== null && raw !== '' && raw !== '\u2014'
        })
        if (!hasContent) return null
        return (
          <div className={styles.card__footer}>
            {footerCols.map((col) => (
              <div key={col.id} className={styles.card__footerItem}>
                {renderValue(col)}
              </div>
            ))}
          </div>
        )
      })()}
    </div>
  )
}

CardListItem.propTypes = {
  row: PropTypes.oneOfType([PropTypes.object, PropTypes.array]).isRequired,
  rowIndex: PropTypes.number.isRequired,
  columns: PropTypes.array.isRequired,
  priorityConfig: PropTypes.object.isRequired,
  onRowClick: PropTypes.func,
  animationDelay: PropTypes.string
}

export default React.memo(CardListItem)
